import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Clock, Calendar, MessageSquare, Mail, Phone } from 'lucide-react';
export function ContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const calculateProgress = () => {
    let filled = 0;
    if (formData.name.trim()) filled++;
    if (formData.business.trim()) filled++;
    if (formData.email.trim()) filled++;
    if (formData.phone.trim()) filled++;
    if (formData.service.trim()) filled++;
    return filled / 5 * 100;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('submitting');
    // Simulate form submission
    setTimeout(() => {
      setFormStatus('success');
      // Reset form after a delay
      setTimeout(() => setFormStatus('idle'), 4000);
    }, 1500);
  };
  return <div className="pt-24 min-h-screen flex flex-col" data-id="element-415">
      {/* Page Header */}
      <section className="py-16 bg-[#0a0a0a] relative overflow-hidden" data-id="element-416">
        <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" data-id="element-417"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-[400px] bg-brand-emerald/15 blur-[120px] rounded-full pointer-events-none" data-id="element-418"></div>

        {/* Decorative Network SVG */}
        <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-419">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" data-id="element-420">
            <motion.g animate={{
            y: [-15, 15, -15],
            opacity: [0.2, 0.6, 0.2]
          }} transition={{
            duration: 7,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-421">
              <circle cx="80%" cy="20%" r="3" fill="#10b981" data-id="element-422" />
              <circle cx="85%" cy="35%" r="2" fill="#10b981" data-id="element-423" />
              <line x1="80%" y1="20%" x2="85%" y2="35%" stroke="#10b981" strokeWidth="1" opacity="0.4" data-id="element-424" />
            </motion.g>
            <motion.g animate={{
            y: [15, -15, 15],
            opacity: [0.2, 0.6, 0.2]
          }} transition={{
            duration: 9,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2
          }} data-id="element-425">
              <circle cx="20%" cy="70%" r="4" fill="#10b981" data-id="element-426" />
              <circle cx="10%" cy="60%" r="2" fill="#10b981" data-id="element-427" />
              <line x1="20%" y1="70%" x2="10%" y2="60%" stroke="#10b981" strokeWidth="1" opacity="0.4" data-id="element-428" />
            </motion.g>
          </svg>
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-429">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-430">
            <h1 className="text-5xl md:text-7xl font-black text-white mb-8 tracking-tighter" data-id="element-431">
              Hablemos de tu <span className="text-gradient" data-id="element-432">proyecto</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 leading-relaxed font-medium" data-id="element-433">
              Da el primer paso para transformar la presencia digital de tu
              negocio. Sin compromisos, solo una conversación sobre cómo podemos
              ayudarte a crecer.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="pb-32 bg-[#0a0a0a] flex-1 relative" data-id="element-434">
        <div className="container mx-auto px-6 md:px-12 max-w-6xl relative z-10" data-id="element-435">
          <div className="bg-gradient-to-br from-[#151515] to-[#0a0a0a] rounded-[3rem] overflow-hidden border border-white/10 shadow-[0_0_50px_rgba(0,0,0,0.5)] flex flex-col lg:flex-row relative" data-id="element-436">
            {/* Left Side - Info */}
            <div className="w-full lg:w-5/12 p-10 lg:p-16 flex flex-col relative overflow-hidden bg-gradient-to-br from-[#111] to-[#0a1a12] border-r border-white/5" data-id="element-437">
              <div className="absolute top-0 right-0 w-96 h-96 bg-brand-emerald/10 blur-[100px] rounded-full pointer-events-none" data-id="element-438"></div>
              <div className="absolute inset-0 bg-grid-pattern-emerald opacity-10" data-id="element-439"></div>

              <motion.div initial={{
              opacity: 0,
              x: -30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="relative z-10 h-full flex flex-col" data-id="element-440">
                <h2 className="text-3xl md:text-4xl font-black text-white mb-10 tracking-tighter leading-tight" data-id="element-441">
                  ¿Qué pasa después de enviar este formulario?
                </h2>

                <div className="space-y-10 mb-12 flex-1" data-id="element-442">
                  <div className="flex gap-5 group" data-id="element-443">
                    <div className="icon-premium w-14 h-14 shrink-0 group-hover:scale-110 transition-transform" data-id="element-444">
                      <Clock size={24} data-id="element-445" />
                    </div>
                    <div data-id="element-446">
                      <h4 className="text-white font-bold mb-2 text-xl tracking-tight" data-id="element-447">
                        Revisión rápida
                      </h4>
                      <p className="text-base text-gray-400 leading-relaxed font-medium" data-id="element-448">
                        Analizamos la información de tu negocio en menos de 24
                        horas laborables.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-5 group" data-id="element-449">
                    <div className="icon-premium w-14 h-14 shrink-0 group-hover:scale-110 transition-transform" data-id="element-450">
                      <MessageSquare size={24} data-id="element-451" />
                    </div>
                    <div data-id="element-452">
                      <h4 className="text-white font-bold mb-2 text-xl tracking-tight" data-id="element-453">
                        Primer contacto
                      </h4>
                      <p className="text-base text-gray-400 leading-relaxed font-medium" data-id="element-454">
                        Te contactamos para presentarnos y entender mejor tus
                        necesidades específicas.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-5 group" data-id="element-455">
                    <div className="icon-premium w-14 h-14 shrink-0 group-hover:scale-110 transition-transform" data-id="element-456">
                      <Calendar size={24} data-id="element-457" />
                    </div>
                    <div data-id="element-458">
                      <h4 className="text-white font-bold mb-2 text-xl tracking-tight" data-id="element-459">
                        Llamada de diagnóstico
                      </h4>
                      <p className="text-base text-gray-400 leading-relaxed font-medium" data-id="element-460">
                        Agendamos una breve reunión gratuita para proponerte la
                        mejor solución.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-white/5 rounded-2xl border border-white/10 backdrop-blur-md" data-id="element-461">
                  <div className="flex items-center gap-3 mb-3" data-id="element-462">
                    <CheckCircle2 className="text-brand-emerald" size={24} data-id="element-463" />
                    <span className="text-white font-bold text-lg" data-id="element-464">
                      100% Confidencial
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 font-medium" data-id="element-465">
                    Tus datos están seguros con nosotros. Nunca enviaremos spam
                    ni compartiremos tu información.
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Right Side - Form */}
            <div className="w-full lg:w-7/12 p-10 lg:p-16 relative" data-id="element-466">
              <div className="absolute inset-0 bg-noise opacity-[0.02] mix-blend-overlay pointer-events-none" data-id="element-467"></div>

              <motion.form initial={{
              opacity: 0,
              x: 30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} onSubmit={handleSubmit} className="space-y-6 relative z-10" data-id="element-468">
                {/* Form Progress */}
                <div className="mb-8" data-id="element-469">
                  <div className="flex justify-between items-center mb-2" data-id="element-470">
                    <span className="text-xs font-bold text-gray-400 uppercase tracking-widest" data-id="element-471">
                      Progreso
                    </span>
                    <span className="text-xs font-bold text-brand-emerald" data-id="element-472">
                      {Math.round(calculateProgress())}%
                    </span>
                  </div>
                  <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-473">
                    <motion.div className="h-full bg-brand-emerald" initial={{
                    width: 0
                  }} animate={{
                    width: `${calculateProgress()}%`
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-474" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-475">
                  <div className="space-y-2" data-id="element-476">
                    <label htmlFor="name" className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2" data-id="element-477">
                      Nombre completo *
                      {formData.name.trim() && <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-478" />}
                    </label>
                    <input type="text" id="name" required value={formData.name} onChange={e => setFormData({
                    ...formData,
                    name: e.target.value
                  })} className="w-full px-5 py-4 rounded-xl border border-white/10 focus:ring-2 focus:ring-brand-emerald/50 focus:border-brand-emerald focus:shadow-[0_0_15px_rgba(16,185,129,0.15)] outline-none transition-all bg-[#111] text-white placeholder-gray-600 font-medium shadow-inner" placeholder="Tu nombre" data-id="element-479" />
                  </div>

                  <div className="space-y-2" data-id="element-480">
                    <label htmlFor="business" className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2" data-id="element-481">
                      Nombre del negocio *
                      {formData.business.trim() && <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-482" />}
                    </label>
                    <input type="text" id="business" required value={formData.business} onChange={e => setFormData({
                    ...formData,
                    business: e.target.value
                  })} className="w-full px-5 py-4 rounded-xl border border-white/10 focus:ring-2 focus:ring-brand-emerald/50 focus:border-brand-emerald focus:shadow-[0_0_15px_rgba(16,185,129,0.15)] outline-none transition-all bg-[#111] text-white placeholder-gray-600 font-medium shadow-inner" placeholder="Ej. Restaurante El Sabor" data-id="element-483" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-484">
                  <div className="space-y-2" data-id="element-485">
                    <label htmlFor="email" className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2" data-id="element-486">
                      <Mail size={14} className="text-brand-emerald/60" data-id="element-487" />
                      Correo electrónico *
                      {formData.email.trim() && <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-488" />}
                    </label>
                    <input type="email" id="email" required value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} className="w-full px-5 py-4 rounded-xl border border-white/10 focus:ring-2 focus:ring-brand-emerald/50 focus:border-brand-emerald focus:shadow-[0_0_15px_rgba(16,185,129,0.15)] outline-none transition-all bg-[#111] text-white placeholder-gray-600 font-medium shadow-inner" placeholder="tu@email.com" data-id="element-489" />
                  </div>

                  <div className="space-y-2" data-id="element-490">
                    <label htmlFor="phone" className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2" data-id="element-491">
                      <Phone size={14} className="text-brand-emerald/60" data-id="element-492" />
                      Teléfono móvil *
                      {formData.phone.trim() && <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-493" />}
                    </label>
                    <input type="tel" id="phone" required value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} className="w-full px-5 py-4 rounded-xl border border-white/10 focus:ring-2 focus:ring-brand-emerald/50 focus:border-brand-emerald focus:shadow-[0_0_15px_rgba(16,185,129,0.15)] outline-none transition-all bg-[#111] text-white placeholder-gray-600 font-medium shadow-inner" placeholder="+34 600 000 000" data-id="element-494" />
                  </div>
                </div>

                <div className="space-y-2" data-id="element-495">
                  <label htmlFor="service" className="text-sm font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2" data-id="element-496">
                    ¿A qué se dedica tu negocio? *
                    {formData.service.trim() && <CheckCircle2 size={14} className="text-brand-emerald" data-id="element-497" />}
                  </label>
                  <select id="service" required value={formData.service} onChange={e => setFormData({
                  ...formData,
                  service: e.target.value
                })} className="w-full px-5 py-4 rounded-xl border border-white/10 focus:ring-2 focus:ring-brand-emerald/50 focus:border-brand-emerald focus:shadow-[0_0_15px_rgba(16,185,129,0.15)] outline-none transition-all bg-[#111] text-white appearance-none font-medium shadow-inner cursor-pointer" data-id="element-498">
                    <option value="" disabled className="text-gray-500" data-id="element-499">
                      Selecciona una opción
                    </option>
                    <option value="restaurante" className="text-gray-900" data-id="element-500">
                      Restaurante / Cafetería / Hostelería
                    </option>
                    <option value="servicios" className="text-gray-900" data-id="element-501">
                      Servicios (Peluquería, Clínica, Taller, etc.)
                    </option>
                    <option value="tienda" className="text-gray-900" data-id="element-502">
                      Tienda Local / Comercio
                    </option>
                    <option value="marca_personal" className="text-gray-900" data-id="element-503">
                      Marca Personal / Profesional Independiente
                    </option>
                    <option value="otro" className="text-gray-900" data-id="element-504">
                      Otro sector
                    </option>
                  </select>
                </div>

                <div className="space-y-2" data-id="element-505">
                  <label htmlFor="message" className="text-sm font-bold text-gray-300 uppercase tracking-wider" data-id="element-506">
                    Cuéntanos sobre tu proyecto (Opcional)
                  </label>
                  <textarea id="message" rows={5} value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} className="w-full px-5 py-4 rounded-xl border border-white/10 focus:ring-2 focus:ring-brand-emerald/50 focus:border-brand-emerald focus:shadow-[0_0_15px_rgba(16,185,129,0.15)] outline-none transition-all bg-[#111] text-white placeholder-gray-600 resize-none font-medium shadow-inner" placeholder="¿Qué objetivo principal quieres conseguir? ¿Tienes web actualmente?..." data-id="element-507"></textarea>
                </div>

                <button type="submit" disabled={formStatus !== 'idle'} className="w-full bg-brand-emerald text-white py-5 rounded-xl font-bold text-xl hover:bg-brand-emeraldLight transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed shadow-[0_0_30px_rgba(16,185,129,0.3)] hover:shadow-[0_0_40px_rgba(16,185,129,0.5)] hover:-translate-y-1 group mt-8 relative overflow-hidden" data-id="element-508">
                  {formStatus === 'idle' && <>
                      <span className="relative z-10" data-id="element-509">
                        Solicitar mi proyecto
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-510"></div>
                      <ArrowRight size={24} className="group-hover:translate-x-1 transition-transform relative z-10" data-id="element-511" />
                    </>}
                  {formStatus === 'submitting' && 'Procesando solicitud...'}
                  {formStatus === 'success' && '¡Solicitud enviada con éxito!'}
                </button>

                <p className="text-center text-sm text-gray-500 mt-6 font-medium" data-id="element-512">
                  Sin compromiso. Sin spam. Solo una conversación sobre cómo
                  hacer crecer tu negocio.
                </p>
              </motion.form>
            </div>
          </div>
        </div>
      </section>
    </div>;
}